import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  DatabaseIcon,
  GlobeIcon,
  NavigationArrowIcon,
  LockIcon,
  LockOpenIcon,
  BugIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";

import { vConsole } from "@/debug/vConsole";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { type Locale } from "@/utils/locale";
import { devLocaleStorage } from "@/utils/localStorage";

import type { ActivePanel } from "./DevToolsHub";
import type { UsePurchaseBlockReturn } from "./tools/usePurchaseBlock";

interface DevToolsMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  activePanel: ActivePanel;
  onTogglePanel: (panel: NonNullable<ActivePanel>) => void;
  purchaseBlock: UsePurchaseBlockReturn;
}

export function DevToolsMenu({
  anchorEl,
  onClose,
  activePanel,
  onTogglePanel,
  purchaseBlock: {
    isPurchaseAllowed,
    purchaseAllowedSecondsLeft,
    togglePurchaseBlock,
  },
}: DevToolsMenuProps) {
  const { locale } = useLocaleInfo();
  const nextLocale: Locale = locale === "ar" ? "en" : "ar";

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{
        zIndex: 100000,
        "& .MuiPaper-root": {
          borderRadius: "var(--radius-2xl)",
          boxShadow: "var(--shadow-2xl)",
        },
      }}
      dir="ltr"
      disableScrollLock
      slotProps={{ list: { dense: true } }}
      transformOrigin={{ horizontal: "right", vertical: "bottom" }}
      anchorOrigin={{ horizontal: "right", vertical: "top" }}
    >
      {!!vConsole && (
        <MenuItem
          selected={activePanel === "console"}
          onClick={() => onTogglePanel("console")}
        >
          <ListItemIcon>
            <BugIcon size={20} />
          </ListItemIcon>
          <ListItemText primary="Console" />
        </MenuItem>
      )}

      <MenuItem
        selected={activePanel === "rq"}
        onClick={() => onTogglePanel("rq")}
      >
        <ListItemIcon>
          <DatabaseIcon size={20} />
        </ListItemIcon>
        <ListItemText primary="React Query" />
      </MenuItem>

      <MenuItem
        selected={activePanel === "router"}
        onClick={() => onTogglePanel("router")}
      >
        <ListItemIcon>
          <NavigationArrowIcon size={20} />
        </ListItemIcon>
        <ListItemText primary="Router" />
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={() => {
          devLocaleStorage.set(nextLocale);
          window.location.reload();
        }}
      >
        <ListItemIcon>
          <GlobeIcon size={20} />
        </ListItemIcon>
        <ListItemText
          primary="Language"
          secondary={`${locale.toUpperCase()} → ${nextLocale.toUpperCase()}`}
        />
      </MenuItem>

      <MenuItem
        onClick={() => {
          onClose();
          togglePurchaseBlock();
        }}
      >
        <ListItemIcon>
          {isPurchaseAllowed ? (
            <LockOpenIcon size={20} className="text-semantic-error-strong" />
          ) : (
            <LockIcon size={20} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            isPurchaseAllowed ? "Purchases allowed" : "Purchases blocked"
          }
          secondary={
            isPurchaseAllowed
              ? `Disables in ${dayjs.duration(purchaseAllowedSecondsLeft, "seconds").format("mm:ss")}`
              : "Tap to unlock"
          }
          slotProps={{
            primary: {
              sx: {
                ...(isPurchaseAllowed && {
                  color: "var(--color-semantic-error-strong)",
                }),
              },
            },
          }}
        />
      </MenuItem>
    </Menu>
  );
}
